/**
 * CSV export utility for the protocol stack.
 * Exports all protocol data attributes including status and metadata.
 */

import { 
  getEffectivePrimaryCategory, 
  getEffectiveSecondaryCategory 
} from './healthCategories';

const CSV_COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Compound Type' },
  { key: 'primaryCategory', label: 'Primary Health Category', getValue: (p) => getEffectivePrimaryCategory(p) },
  { key: 'secondaryCategory', label: 'Secondary Health Category', getValue: (p) => getEffectiveSecondaryCategory(p) || '' },
  { key: 'cycleStatus', label: 'Status' },
  { key: 'startDate', label: 'Start Date' },
  { key: 'endDate', label: 'End Date' },
  { key: 'dosageMin', label: 'Dosage Min' },
  { key: 'dosageMax', label: 'Dosage Max' },
  { key: 'weeklyTotalDosage', label: 'Weekly Total Dosage' },
  { key: 'unit', label: 'Unit' },
  { key: 'administrationFormat', label: 'Administration Format' },
  { key: 'timingOfDay', label: 'Timing of Day' },
  { key: 'frequencySchedule', label: 'Frequency Schedule' },
  { key: 'brandOrSupplier', label: 'Brand or Supplier' },
  { key: 'intendedOutcome', label: 'Intended Outcome' },
  { key: 'notes', label: 'Notes' },
  { key: 'userId', label: 'User ID' },
  { key: 'createdAt', label: 'Created At' },
  { key: 'updatedAt', label: 'Updated At' }
];

function formatCsvValue(value) {
  if (value === null || value === undefined) {
    return '';
  }

  let stringValue = String(value);

  // CSV formula injection defense: neutralize cells that spreadsheet
  // apps would interpret as formulas (leading =, +, -, @, tab, or CR).
  if (/^[=+\-@\t\r]/.test(stringValue)) {
    stringValue = `'${stringValue}`;
  }

  // Escape values that contain CSV special characters
  if (
    stringValue.includes(',') ||
    stringValue.includes('"') ||
    stringValue.includes('\n') ||
    stringValue.includes('\r')
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

function buildCsvRow(values) {
  return values.map(formatCsvValue).join(',');
}

function downloadCsv(filename, csvContent) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

function buildCsvContent(protocols) {
  const headerRow = buildCsvRow(CSV_COLUMNS.map((col) => col.label));
  const dataRows = protocols.map((protocol) => {
    const values = CSV_COLUMNS.map((col) => {
      const value = col.getValue ? col.getValue(protocol) : protocol[col.key];
      return value;
    });
    return buildCsvRow(values);
  });

  return [headerRow, ...dataRows].join('\n');
}

import { formatCategoryLabel, filterProtocolsByStatusCategory } from './sortUtils';

function filterProtocols(protocols = [], options = {}) {
  return filterProtocolsByStatusCategory(protocols, options);
}

function buildFilename(options = {}) {
  const { status, category, categories } = options;
  const parts = ['supps-and-peps'];

  if (status && status !== 'All') {
    parts.push(status.toLowerCase());
  }

  const categoryLabel = formatCategoryLabel(categories, category);
  if (categoryLabel && categoryLabel !== 'All Compounds') {
    parts.push(categoryLabel.toLowerCase().replace(/&/g, '').replace(/\s+/g, '-').replace(/--+/g, '-'));
  }

  parts.push('stack.csv');
  return parts.join('-');
}

export function exportStackToCsv(protocols, options = {}) {
  if (!Array.isArray(protocols) || protocols.length === 0) {
    return;
  }

  const filtered = filterProtocols(protocols, options);
  if (filtered.length === 0) {
    return;
  }

  const csvContent = buildCsvContent(filtered);
  const filename = buildFilename(options);
  downloadCsv(filename, csvContent);
}
